import { useEffect, useState } from 'react';

import type { ExamListing } from '../../domain/examCatalogue';
import { useT } from '../../i18n';
import { deleteExam, examLoeschHindernis } from '../../persistence/importExam';
import { getStore } from '../../persistence/store';
import { appendAppLog } from '../../platform/appLog';
import { archiviereExamDateien, archiviereStationsmaterial } from '../../platform/archive';
import { Dialog } from '../../ui/Dialog';
import styles from './SetupSheet.module.css';

/**
 * Rückfrage vor dem Löschen einer Prüfung.
 *
 * ⚠️ **Sie erscheint nur, wenn wirklich etwas daran hängt.** Eine Prüfung ohne eine einzige
 * Protokollzeile wird sofort gelöscht — eine Rückfrage, die nichts zu warnen hat, erzieht
 * dazu, Rückfragen wegzutippen.
 *
 * Hängt etwas daran, nennt der Text zwei Zahlen getrennt: wie viele Prüflinge bewertet sind,
 * und wie viele Einträge **noch nicht übertragen** sind. Nur die zweite beschreibt einen
 * unwiederbringlichen Verlust; alles Übertragene liegt auch auf dem Server.
 *
 * ⚠️ **Die Aufnahmen werden nicht gelöscht, sondern archiviert.** Fotos, Markierebenen, Ton,
 * Video, Handschrift und Bildschirmabzüge wandern nach `old/<Löschdatum>/logs/…` und
 * verschwinden erst nach der eingestellten Frist (`platform/archive.ts`). Zwei Stufen, weil
 * eine Aufnahme Beweismaterial ist: ein versehentliches Löschen wäre sonst endgültig.
 */
export function DeleteExamDialog({
  ziel,
  onClose,
  onDeleted,
}: {
  ziel: ExamListing;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const t = useT();
  const [fehler, setFehler] = useState<string | null>(null);
  const [hindernis, setHindernis] = useState<{
    gesamt: number;
    nichtÜbertragen: number;
    prüflinge: number;
  } | null>(null);
  const [läuft, setLäuft] = useState(false);

  /**
   * Erst die Dateien beiseite, dann die Zeilen.
   *
   * Diese Reihenfolge ist Absicht: bricht das Archivieren ab, steht die Prüfung noch in der
   * Ablage und der Prüfer kann es erneut versuchen. Andersherum wären die Zeilen weg und die
   * Dateien lägen unauffindbar im alten Ordner.
   */
  const loeschen = async () => {
    await archiviereExamDateien(ziel.id, appendAppLog);
    // Das Stationsmaterial liegt nicht unter `logs/`, sondern unter `media/<Prüfung>/` — ohne
    // diesen Griff bliebe es auf dem Gerät, nachdem die Prüfung verschwunden ist.
    await archiviereStationsmaterial(ziel.id, appendAppLog);
    await deleteExam(getStore(), ziel.id);
  };

  useEffect(() => {
    let aktuell = true;
    void (async () => {
      const h = await examLoeschHindernis(getStore(), ziel.id);
      if (!aktuell) return;
      if (h.gesamt === 0) {
        // Nichts daran — ohne Rückfrage weg. Etwaige Aufnahmen wandern trotzdem ins Archiv:
        // eine Prüfung ohne Protokollzeile kann sehr wohl Dateien haben, wenn der Import
        // nach der Aufnahme scheiterte.
        await loeschen();
        if (aktuell) onDeleted();
        return;
      }
      setHindernis(h);
    })();
    return () => {
      aktuell = false;
    };
  }, [ziel.id, onDeleted]);

  // Solange nicht feststeht, ob überhaupt etwas daran hängt, wird nichts gezeigt: ein Blatt,
  // das sofort wieder verschwindet, wirkt wie ein Fehler.
  if (!hindernis) return null;

  return (
    <Dialog
      kicker={t.setup.exam}
      title={t.setup.deleteTitle}
      onClose={onClose}
      width={480}
      dismissible={!läuft}
    >
      <p className={styles.deleteBody}>{t.setup.deleteBody(ziel.name)}</p>
      <p className={styles.deleteBody}>{t.setup.deleteArchiveNote}</p>
      {fehler && <p className={styles.deleteWarn} data-schwer="true">{fehler}</p>}
      <p
        className={styles.deleteWarn}
        data-schwer={hindernis.nichtÜbertragen > 0}
      >
        {t.setup.deleteRatings(hindernis.prüflinge, hindernis.nichtÜbertragen)}
      </p>
      <div className={styles.deleteButtons}>
        <button
          type="button"
          className={`tap ${styles.deleteCancel}`}
          disabled={läuft}
          onClick={onClose}
        >
          {t.setup.deleteCancel}
        </button>
        <button
          type="button"
          className={`tap ${styles.deleteConfirm}`}
          disabled={läuft}
          onClick={() => {
            setLäuft(true);
            void (async () => {
              try {
                await loeschen();
                onDeleted();
              } catch (fehler) {
                // Ohne Meldung sähe der Prüfer nur, dass nichts geschieht, und tippte weiter.
                void appendAppLog('Löschen der Prüfung ' + ziel.id + ' gescheitert: ' + String(fehler), 'warn');
                setFehler(String(fehler));
              } finally {
                setLäuft(false);
              }
            })();
          }}
        >
          {t.setup.deleteConfirm}
        </button>
      </div>
    </Dialog>
  );
}
